import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { FormikHelpers } from 'formik';
import { styled } from 'dripsy';

import { ButtonsLayout } from '../layouts/ButtonsLayout';
import {
  TrackFeedbackFormDataType,
  trackFeedbackFormSchema,
} from '@validation';
import { BaseText } from '../common/BaseText';
import { Form } from './Form';
import { Separator } from '../common/Separator';
import { Field } from './Field';
import { MultilineField } from './MultilineField';
import { FormButton } from './FormButton';
import { Button, ButtonStyleEnum } from '../common/Button';

const Text = styled(BaseText)({
  flexShrink: 1,
  color: '$text400',
  variant: 'text.medium',
});

const QuestionText = styled(BaseText)({
  variant: 'text.medium',
  color: '$heading800',
});

export interface ITrackFeedbackFormProps {
  onSubmit: (
    values: TrackFeedbackFormDataType,
    formik: FormikHelpers<TrackFeedbackFormDataType>,
  ) => void;
  onCancel: () => void;
  isFormSubmitting: boolean;
}

export const TrackFeedbackForm = observer<ITrackFeedbackFormProps>(
  ({ onSubmit, onCancel, isFormSubmitting }) => {
    const { t } = useTranslation();

    const initialValues: TrackFeedbackFormDataType = {
      trackLink: '',
      referenceLink: '',
      aspectsDescription: '',
    };

    return (
      <Form<TrackFeedbackFormDataType>
        initialValues={initialValues}
        validationSchema={trackFeedbackFormSchema}
        onSubmit={onSubmit}
      >
        <Text>{t('common.trackFeedbackForm.description')}</Text>
        <Separator />
        <Field
          name="trackLink"
          placeholder={t('common.trackFeedbackForm.privateLink')}
          autoCapitalize="none"
          keyboardType="email-address"
        />
        <Separator />
        <Field
          name="referenceLink"
          placeholder={t('common.trackFeedbackForm.trackLink')}
          autoCapitalize="none"
        />
        <Separator size="$9" />
        <QuestionText>
          {t('common.trackFeedbackForm.questionText')}
        </QuestionText>
        <Separator />
        <MultilineField
          name="aspectsDescription"
          placeholder={t('common.trackFeedbackForm.question')}
          minHeight={130}
        />

        <Separator size="$13" />

        <ButtonsLayout>
          <FormButton isLoading={isFormSubmitting}>
            {t('common.trackFeedbackForm.send')}
          </FormButton>
          <Button
            buttonStyle={ButtonStyleEnum.Ghost}
            onPress={onCancel}
          >
            {t('common.trackFeedbackForm.cancel')}
          </Button>
        </ButtonsLayout>
      </Form>
    );
  },
);
